import React, { useEffect, useState } from "react";
import { Grid, MenuItem, Paper, useMediaQuery, useTheme } from "@mui/material";
import BarekamText from "../components/text";
import BarekamInput from "../components/input";
import { BarekamSelect } from "../components/select/select";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { CalculateContainer, CalculatePageContainer, PaymentInfoDetailsBlock, VolumeContainer } from "./styled";
import { getCountries, getCurrencies, getShippingMethods, } from "../../redux/slices/static-data.slice";
import BarekamButton, { CalculateButtons } from "../components/button";
import { changeSidebarInOrders, changeSidebarWithNavigate } from "../../redux/slices/sidebar.slice";
import { changeActionbarType } from "../../redux/slices/actionbar.slice";
import { ActionbarType } from "../../redux/reducers/actionbar-reducers";
import { CurrencyModel } from "../../redux/reducers/static-data.reducers";
import Message from "../components/message";
import { MessageSections } from "../components/message/message";
import { ShippingMethodSelector } from "../orders/order-popup/shipping-method-selector/shipping-method-selector";
import { calculateForLand, getVolumeMultiplier } from "./calculate-functions";
import { ActionBar } from "../components/action-bar/action-bar";
import { ARMENIA_COUNTRY_ID } from "../../constants";
import { getMessage } from "../shared/helper-functions";

let count = 0;

const Calculate = ({inModal = false}) => {
    const theme = useTheme();
    const { countries, currencies } = useAppSelector(state => state.staticDataReducer);

    const [calculationData, setCalculationData] = useState<CalculationData>({} as CalculationData);
    const [receiverCountryIds, setReceiverCountryIds] = useState<number[]>([]);
    const [currency, setCurrency] = useState<CurrencyModel>();

    const [deliveryData, setDeliveryData] = useState<DeliveryData>({
        price: 0,
        volumetricPrice: 0,
        shippingTimeInDays: 0,
        weight: 0
    });

    const [isReady, setIsReady] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});


    const { messageCache, languageId } = useAppSelector(state => state.messagesReducer);

    const matchesPhone =
    useMediaQuery(theme.breakpoints.between(0, 1200)) ||
    window.innerHeight <= 500;

    const dispatch = useAppDispatch();

    function getRenderValue(countryId: number) {
        const selectedCountry = countryId
            ? countries.find(country => country.countryId === countryId)
            : null;

        return selectedCountry ? selectedCountry.name[languageId] : 'country';
    }

    function handleChange(field: 'senderCountryId' | 'receiverCountryId' | 'weight' | 'length' | 'width' | 'height', value: number) {
        setCalculationData(prevState => {
            setDeliveryData({ price: 0, volumetricPrice: 0, shippingTimeInDays: 0, weight: 0 });

            if (field === 'senderCountryId') {
                const senderIsArmenia = value === ARMENIA_COUNTRY_ID;
                const senderIsEurope = value !== ARMENIA_COUNTRY_ID;

                if (senderIsArmenia) {
                    setReceiverCountryIds(countries
                        .filter(country => country.countryId !== ARMENIA_COUNTRY_ID)
                        .map(country => country.countryId))

                    prevState.receiverCountryId = countries.find(country => country.countryId > 1)?.countryId;
                    setCurrency(currencies.find(currency => currency.currencyId === 2));
                } else if (senderIsEurope) {
                    setReceiverCountryIds(countries
                        .filter(country => country.countryId === ARMENIA_COUNTRY_ID)
                        .map(country => country.countryId))

                    prevState.receiverCountryId = ARMENIA_COUNTRY_ID;
                    setCurrency(currencies.find(currency => currency.currencyId === 2));
                }
            }

            prevState[field] = value <= 0 ? undefined : value;

            if (field === 'width' || field === 'height' || field === 'length') {
                // prevState.volume = (prevState.width || 0) * (prevState.height || 0) * (prevState.length || 0) / 5000
                const sum = getVolumeMultiplier((prevState.width || 0) * (prevState.height || 0) * (prevState.length || 0), currency?.currencyId || 1)
                prevState.volume = +(Math.round(sum * 100) / 100).toFixed(2);
            }

            return { ...prevState };
        })
    }

    function getErrors() {
        const actualErrors: { [key: string]: string } = {};

        if (!calculationData.senderCountryId) {
            actualErrors.senderCountry = 'required';
        }
        if (!calculationData.receiverCountryId) {
            actualErrors.receiverCountry = 'required';
        }
        if (!calculationData.weight && !calculationData.volume) {
            actualErrors.weight = 'required';
        }
        if (calculationData.height || calculationData.width || calculationData.length) {
            if (!calculationData.height) {
                actualErrors.height = 'required';
            }
            if (!calculationData.width) {
                actualErrors.width = 'required';
            }
            if (!calculationData.length) {
                actualErrors.length = 'required';
            }
        }

        setErrors(actualErrors)

        return actualErrors;
    }

    function calculate() {
        const errors = getErrors();

        if (!Object.keys(errors).length && currency) {
            setDeliveryData(data => {
                console.log(calculationData);
                data.shippingTimeInDays = 25;
                data.weight = calculationData.weight || calculationData.volume || 0;

                const senderWarehouse = countries
                    .find(country => country.countryId === calculationData.senderCountryId)
                    ?.warehouse[0];

                const { price, volumetricPrice } = senderWarehouse
                    ? calculateForLand(data.weight, calculationData.volume, senderWarehouse, currency)
                    : { price: 0, volumetricPrice: 0 }

                data.price = price;
                data.volumetricPrice = volumetricPrice;

                return data;
            })
        } else {
            setDeliveryData(
                { price: 0, volumetricPrice: 0, shippingTimeInDays: 0, weight: 0 }
            );
        }
    }

    useEffect(() => {
        if(count === 0) {
            dispatch(getCountries());
            dispatch(getCurrencies());
            dispatch(getShippingMethods()),
            dispatch(changeSidebarInOrders(false));
            dispatch(changeSidebarWithNavigate(true));
            dispatch(changeActionbarType(ActionbarType.CALCULATE));
        }
        count++;
    }, [])

    useEffect(() => {
        if (countries.length && currencies.length) {
            setReceiverCountryIds(countries
                .filter(country => country.countryId !== ARMENIA_COUNTRY_ID)
                .map(country => country.countryId));
            calculationData.receiverCountryId = countries.find(country => country.countryId > 1)?.countryId;

            setCurrency(currencies.find(currency => currency.currencyId === 2));

            setCalculationData({
                senderCountryId: countries.find(country => country.countryId !== ARMENIA_COUNTRY_ID)?.countryId,
                receiverCountryId: ARMENIA_COUNTRY_ID,
                weight: 0,
                length: 0,
                width: 0,
                height: 0,
                volume: 0
            })
            setIsReady(true);
        }
    }, [countries, currencies])

    if (!isReady) {
        return <></>
    } else {
       
        return (
            <CalculatePageContainer>
                {!inModal && <ActionBar headerText={<Message messageKey={'calculation_title'} sectionId={MessageSections.CALCULATE} />} />}

                <Paper>
                    <CalculateContainer container spacing={2} rowSpacing={4}>
                        <Grid item xs={12}>
                            <ShippingMethodSelector withLink />
                        </Grid>
                        <Grid item xs={matchesPhone ? 12 : 4}>
                            <BarekamSelect
                                label={
                                    <BarekamText variant={'1rem'}>
                                        <Message messageKey={'sender_country'} sectionId={MessageSections.CALCULATE} />
                                    </BarekamText>}
                                variant={'outlined'}
                                displayEmpty={!calculationData.senderCountryId}
                                value={calculationData.senderCountryId}
                                type={"standard"}
                                onChange={(event) => handleChange('senderCountryId', event.target.value as number)}
                                error={!!errors['senderCountry']}
                                errorMessage={errors['senderCountry'] && getMessage(errors['senderCountry'], MessageSections.CALCULATE, messageCache, languageId)}
                                fullWidth
                                required
                            >
                                {countries.map(country =>
                                    <MenuItem value={country.countryId}
                                        key={country.countryId}>
                                        {country.name[languageId]}
                                    </MenuItem>
                                )}
                            </BarekamSelect>
                        </Grid>

                        {/*RECEIVER COUNTRY*/}
                        <Grid item xs={matchesPhone ? 12 : 4}>
                            <BarekamSelect
                                label={<BarekamText variant={'1rem'}>
                                    <Message messageKey={'receiver_country'} sectionId={MessageSections.CALCULATE} />
                                </BarekamText>}
                                variant={'outlined'}
                                displayEmpty={!calculationData.receiverCountryId}
                                renderValue={(countryId) => getRenderValue(countryId as number)}
                                value={calculationData.receiverCountryId}
                                type={"standard"}
                                onChange={(event) => handleChange('receiverCountryId', event.target.value as number)}
                                MenuProps={{
                                    PaperProps: {
                                        square: true,
                                        sx: {
                                            '& .MuiList-root': {
                                                padding: 0,
                                            }
                                        }
                                    }
                                }}
                                error={!!errors['receiverCountry']}
                                errorMessage={errors['receiverCountryId'] && getMessage(errors['receiverCountryId'], MessageSections.CALCULATE, messageCache, languageId)}
                                required>

                                {countries
                                    .filter(country => receiverCountryIds.includes(country.countryId))
                                    .map(country =>
                                        <MenuItem value={country.countryId}
                                            key={country.countryId}>
                                            {country.name[languageId]}
                                        </MenuItem>
                                    )}
                            </BarekamSelect>
                        </Grid>

                        {matchesPhone ? <></> : <Grid item xs={4} />}

                        <Grid item xs={matchesPhone ? 12 : 4}>
                            <BarekamInput value={calculationData.weight || calculationData.volume || ''}
                                placeholder={getMessage('weight_kg', MessageSections.CALCULATE, messageCache, languageId)}
                                label={getMessage('weight_kg', MessageSections.CALCULATE, messageCache, languageId)}
                                type={"number"}
                                customInputVariant={"standard"}
                                width={'100%'}
                                onChange={(event) => handleChange('weight', +event.target.value)}
                                error={!!errors['weight']}
                                errorMessage={errors['weight'] && getMessage(errors['weight'], MessageSections.CALCULATE, messageCache, languageId)}
                                required />
                        </Grid>

                        {matchesPhone ? <></> : <Grid item xs={8} />}

                        <Grid item xs={matchesPhone ? 12 : 3}>

                            <BarekamInput value={calculationData.length || ''}
                                placeholder={getMessage('calculate_length_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                label={getMessage('calculate_length_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                type={"number"}
                                customInputVariant={"standard"}
                                width={'100%'}
                                error={!!errors['length']}
                                errorMessage={errors['length'] && getMessage(errors['length'], MessageSections.CALCULATE, messageCache, languageId)}
                                onChange={(event) => handleChange('length', +event.target.value)} />
                        </Grid>

                        <Grid item xs={matchesPhone ? 12 : 3}>

                            <BarekamInput value={calculationData.width || ''}
                                placeholder={getMessage('calculate_width_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                label={getMessage('calculate_width_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                type={"number"}
                                customInputVariant={"standard"}
                                width={'100%'}
                                error={!!errors['width']}
                                errorMessage={errors['width'] && getMessage(errors['width'], MessageSections.CALCULATE, messageCache, languageId)}
                                onChange={(event) => handleChange('width', +event.target.value)} />
                        </Grid>

                        <Grid item xs={matchesPhone ? 12 : 3}>

                            <BarekamInput value={calculationData.height || ''}
                                type={"number"}
                                placeholder={getMessage('calculate_height_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                label={getMessage('calculate_height_cm', MessageSections.CALCULATE, messageCache, languageId)}
                                customInputVariant={"standard"}
                                width={'100%'}
                                error={!!errors['height']}
                                errorMessage={errors['height'] && getMessage(errors['height'], MessageSections.CALCULATE, messageCache, languageId)}
                                onChange={(event) => handleChange('height', +event.target.value)} />
                        </Grid>

                        <Grid item xs={matchesPhone ? 12 : 3}>
                            <VolumeContainer>
                                <BarekamText variant={'m'} opaque>
                                    <Message messageKey={'volume'} sectionId={MessageSections.CALCULATE} />:
                                </BarekamText>

                                <BarekamText variant={'m'}>
                                    {calculationData.volume}
                                </BarekamText>

                                <BarekamText variant={'m'}>
                                    <Message messageKey={'kg'} sectionId={MessageSections.CALCULATE} />
                                </BarekamText>
                            </VolumeContainer>
                        </Grid>

                        <Grid item xs={12}>
                            <BarekamButton type={CalculateButtons.CALCULATE} onClick={() => calculate()}>
                                <BarekamText variant={'l'} bold>
                                    <Message messageKey={'calculate'} sectionId={MessageSections.CALCULATE} />
                                </BarekamText>
                            </BarekamButton>
                        </Grid>

                        {deliveryData.price ?
                            <Grid item xs={12}>
                                <PaymentInfoDetailsBlock>
                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <BarekamText variant={'m'} opaque>
                                            <Message messageKey={'delivery_duration'}
                                                sectionId={MessageSections.CALCULATE} />:
                                        </BarekamText>

                                        <BarekamText variant={'m'} bold>
                                            {' '}
                                            {deliveryData.shippingTimeInDays}
                                            {' '}
                                            <Message messageKey={'days'} sectionId={MessageSections.CALCULATE} />
                                        </BarekamText>
                                    </div>

                                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                                        <BarekamText variant={'m'} opaque>
                                            <Message messageKey={'delivery_price'}
                                                sectionId={MessageSections.CALCULATE} />:
                                        </BarekamText>

                                        <BarekamText variant={'m'} bold>
                                            {deliveryData.volumetricPrice > deliveryData.price
                                                ? deliveryData.volumetricPrice
                                                : deliveryData.price}
                                            {currency?.sign}
                                        </BarekamText>
                                    </div>

                                </PaymentInfoDetailsBlock>
                            </Grid> : <></>
                        }
                    </CalculateContainer>
                </Paper>
            </CalculatePageContainer>
        );
    }
};

interface CalculationData {
    senderCountryId: number | undefined,
    receiverCountryId: number | undefined,
    weight: number | undefined,

    length: number | undefined,
    width: number | undefined,
    height: number | undefined,
    volume: number
}

interface DeliveryData {
    price: number,
    volumetricPrice: number,
    shippingTimeInDays: number,
    weight: number | undefined
    volume?: number | undefined
}

export default Calculate;
